/**
 * Shuttle User Authentication Utilities
 * Handles user login detection, cookie reading, and user data management
 * 
 * @package A2A_Theme
 * @subpackage Shuttle_Booking
 */

/**
 * ShuttleUserAuth Class
 * 
 * Environment Detection:
 * - Uses window.wpEnv.type from header.php (defined in wp-config.php)
 * - 'local' or 'staging' → _a2alu_test cookie + testbooking.alps2alps.com
 * - 'production' → _a2alu cookie + booking.alps2alps.com
 */
class ShuttleUserAuth {
    constructor() {
        this.cookieName = this.getCookieName();
        this.userData = null;
        this.accessToken = null;
        this.isLoggedIn = false;
        
        // Initialize on construction
        this.init();
    }
    
    /**
     * Initialize authentication state
     */
    init() {
        this.loadUserFromCookie();
        this.handleAuthRedirect();
    }
    
    /**
     * Get the correct cookie name based on environment
     * Uses WP_ENVIRONMENT_TYPE from wp-config.php (via header.php)
     * @returns {string} Cookie name
     */
    getCookieName() {
        // Use wpEnv from header.php
        if (typeof window.wpEnv !== 'undefined' && window.wpEnv.type) {
            // local and staging use _a2alu_test
            // production uses _a2alu
            if (window.wpEnv.type === 'local' || window.wpEnv.type === 'staging') {
                return '_a2alu_test';
            }
            
            if (window.wpEnv.type === 'production') {
                return '_a2alu';
            }
        }
        
        // Fallback to _a2alu_test if wpEnv is not defined
        return '_a2alu_test';
    }
    
    /**
     * Get cookie value by name
     * @param {string} name Cookie name
     * @returns {string|null} Cookie value or null if not found
     */
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        
        return null;
    }
    
    /**
     * Parse the authentication cookie
     * @returns {Object|null} Parsed cookie data or null
     */
    parseCookie() {
        const cookieValue = this.getCookie(this.cookieName);
        
        if (!cookieValue) {
            return null;
        }
        
        try {
            // Decode URL encoding if present
            const decodedValue = decodeURIComponent(cookieValue);
            
            // Parse JSON
            const cookieData = JSON.parse(decodedValue);
            
            // Validate that it's not an empty cookie
            if (!cookieData || typeof cookieData !== 'object') {
                return null;
            }
            
            // Check if cookie has actual data (not just an empty object)
            if (!cookieData.access_token && !cookieData.data) {
                return null;
            }
            
            return cookieData;
            
        } catch (error) {
            console.error('[shuttle-auth] Failed to parse cookie:', error);
            return null;
        }
    }
    
    /**
     * Decode base64 user data from cookie
     * @param {string} base64Data Base64 encoded user data
     * @returns {Object|null} Decoded user data or null
     */
    decodeUserData(base64Data) {
        if (!base64Data) {
            return null;
        }
        
        try {
            // Decode base64
            const jsonString = atob(base64Data);
            
            // Parse JSON
            const userData = JSON.parse(jsonString);
            
            // Validate that we have actual user data
            if (!userData || typeof userData !== 'object') {
                return null;
            }
            
            // Check for essential user fields
            // At minimum, we should have an email or user ID
            if (!userData.email && !userData.id && !userData.user_id) {
                return null;
            }
            
            return userData;
            
        } catch (error) {
            console.error('[shuttle-auth] Failed to decode user data:', error);
            return null;
        }
    }
    
    /**
     * Load user data from cookie
     * @returns {boolean} True if user is logged in
     */
    loadUserFromCookie() {
        // Parse the cookie
        const cookieData = this.parseCookie();
        
        if (!cookieData) {
            this.isLoggedIn = false;
            this.userData = null;
            this.accessToken = null;
            return false;
        }
        
        // Extract access token
        this.accessToken = cookieData.access_token || null;
        
        // Decode and extract user data
        if (cookieData.data) {
            this.userData = this.decodeUserData(cookieData.data);
        } else {
            this.userData = null;
        }
        
        // User is logged in if we have both token and valid user data
        this.isLoggedIn = !!(this.accessToken && this.userData);
        
        return this.isLoggedIn;
    }
    
    /**
     * Check if user is logged in
     * @returns {boolean}
     */
    checkLoginStatus() {
        return this.isLoggedIn;
    }
    
    /**
     * Get user data
     * @returns {Object|null}
     */
    getUserData() {
        return this.userData;
    }
    
    /**
     * Get access token
     * @returns {string|null}
     */
    getAccessToken() {
        return this.accessToken;
    }
    
    /**
     * Get specific user field
     * @param {string} field Field name
     * @param {*} defaultValue Default value if field doesn't exist
     * @returns {*}
     */
    getUserField(field, defaultValue = null) {
        if (!this.userData || !this.userData[field]) {
            return defaultValue;
        }
        
        return this.userData[field];
    }
    
    /**
     * Handle authentication redirect parameters
     * Checks for ?auth=... parameter in URL
     */
    handleAuthRedirect() {
        const urlParams = new URLSearchParams(window.location.search);
        const authParam = urlParams.get('auth');
        
        if (!authParam) {
            return;
        }
        
        try {
            // Parse auth JSON
            const authData = JSON.parse(decodeURIComponent(authParam));
            
            // Handle different auth actions
            switch (authData.auth_action) {
                case 'user_registered':
                    // New account created successfully during checkout
                    this.showSuccessMessage('Account created successfully! ✅');
                    this.loadUserFromCookie();
                    this.triggerLoginStatusChange();
                    break;
                    
                case 'password_hide':
                    // Silent login success - this is the normal login flow
                    // User logged in via password field, just reload data silently
                    this.loadUserFromCookie();
                    this.triggerLoginStatusChange();
                    // No alert/popup - login should be seamless
                    break;
                    
                case 'user_logged_in':
                    // Alternative login success response (if backend uses this)
                    this.loadUserFromCookie();
                    break;
                    
                case 'password_error_show':
                    // Wrong password
                    this.showErrorMessage('Incorrect password. Please try again.');
                    break;
                    
                case 'login_error_show':
                    // Email doesn't exist (trying to login with password)
                    this.showErrorMessage('Account not found. Please check your email.');
                    break;
                    
                case 'registered_user_updated':
                    // Existing registered customer updated during checkout
                    this.showSuccessMessage('Profile updated successfully! ✅');
                    this.loadUserFromCookie();
                    break;
                    
                case 'new_email_entered':
                    // Intermediate state - no action needed
                    break;
                    
                default:
                    // Unhandled auth action
                    break;
            }
            
            // Clean URL by removing auth parameter
            this.cleanAuthUrl();
            
        } catch (error) {
            console.error('Failed to parse auth parameter:', error);
        }
    }
    
    /**
     * Show success message to user
     * @param {string} message Success message
     */
    showSuccessMessage(message) {
        // Simple alert for now - can be replaced with a proper toast/notification later
        if (typeof message === 'string' && message.trim()) {
            alert(message);
        }
    }
    
    /**
     * Show error message to user
     * @param {string} message Error message
     */
    showErrorMessage(message) {
        // Simple alert for now - can be replaced with a proper toast/notification later
        if (typeof message === 'string' && message.trim()) {
            alert(message);
        }
    }
    
    /**
     * Remove auth parameter from URL without page reload
     */
    cleanAuthUrl() {
        const url = new URL(window.location);
        url.searchParams.delete('auth');
        
        // Update URL without reload
        window.history.replaceState({}, document.title, url.toString());
    }
    
    /**
     * Trigger custom event when login status changes
     * This allows other components (like checkout manager) to react to login/logout
     */
    triggerLoginStatusChange() {
        const event = new CustomEvent('shuttleUserLoginStatusChanged', {
            detail: {
                isLoggedIn: this.isLoggedIn,
                userData: this.userData
            }
        });
        
        window.dispatchEvent(event);
    }
    
    /**
     * Log out user (client-side only)
     * Note: This doesn't invalidate the server-side session
     * User needs to be redirected to logout endpoint for full logout
     */
    logout() {
        // Clear local state
        this.isLoggedIn = false;
        this.userData = null;
        this.accessToken = null;
        
        // Note: Cookie is httpOnly on backend, so we can't delete it from JavaScript
        // User must be redirected to backend logout endpoint
    }
    
    /**
     * Get full user info as object
     * @returns {Object}
     */
    getUserInfo() {
        return {
            isLoggedIn: this.isLoggedIn,
            userData: this.userData,
            hasToken: !!this.accessToken,
            email: this.getUserField('email'),
            name: this.getUserField('name') || this.getUserField('first_name'),
            firstName: this.getUserField('first_name') || this.getUserField('firstname'),
            lastName: this.getUserField('last_name') || this.getUserField('lastname'),
            phone: this.getUserField('phone') || this.getUserField('phone_number')
        };
    }
}

// Initialize global instance
window.shuttleUserAuth = new ShuttleUserAuth();

